import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Spinner,
  Button,
  Container,
  Alert,
  Modal,
  Dropdown,
  Accordion,
} from "react-bootstrap";
import {
  getInvoiceById,
  createClaim,
  getClaimByInvoiceId,
  updateClaim,
  updateInvoice,
  sendInvoiceByEmail,
  getBankingDetailsByPracticeNumbers,
  decryptField,
} from "../../afinanceapi";
import { useParams, useLocation } from "react-router-dom";
import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import {
  getAccountById,
  getPatientById,
  getPracticeById,
  getUserById,
} from "../../api";
import { SubmitClaim } from "./SubmitClaim";
import { ReverseClaim } from "./ReverseClaim";
import { useNavigate } from "react-router-dom";
import { ExportPDFInvoice } from "./ExportInvoicePDF";
import CreateReceiptModal from "./Receipt";
import { jwtDecode } from "jwt-decode";
import EmailInvoiceModal from "./EmailInvoice";
import { buildEmailData } from "./BuildInvRecCNData";
import ChangeLiability from "./ChangeLiability";

export default function PostedInvoices(onOpenReceipt) {
  const [patTotal, setPatTotal] = useState(0);
  const [medTotal, setMedTotal] = useState(0);
  const [patReceiptTotal, setPatReceiptTotal] = useState(0);
  const [medReceiptTotal, setMedReceiptTotal] = useState(0);
  const [patCreditTotal, setPatCreditTotal] = useState(0);
  const [medCreditTotal, setMedCreditTotal] = useState(0);
  const [patRefundTotal, setPatRefundTotal] = useState(0);
  const [medRefundTotal, setMedRefundTotal] = useState(0);
  const [invoice, setInvoice] = useState({});
  const [account, setAccount] = useState({});
  const [patient, setPatients] = useState({});
  const [practice, setPractice] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [practitioner, setPractitioner] = useState({});
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const navigate = useNavigate();
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [userId, setUserId] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [message, setMessage] = useState("");
  const [statementBlob, setStatementBlob] = useState(null);
  const [showLineInfo, setShowLineInfo] = useState(false);
  const [selectedClaimLine, setSelectedClaimLine] = useState(null);
  const [banking, setBanking] = useState([]);
  const [manualSubmit, setManualSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [liabilityModalShow, setLiabilityModalShow] = useState(false);
  const [claim, setClaim] = useState({
    status: "Pending Submission",
    transactionNumber: "",
    message: "",
    financials: {
      submittedAmount: "",
      approvedGross: "",
      approvedNett: "",
      discount: "",
      surcharges: "",
      copay: "",
      levy: "",
      overCharged: "",
      feeOverCharged: "",
      calculatedGross: "",
    },
    patientClaimFinancials: {
      claimFinancialDescription: "",
      claimFinancialAmount: "",
    },
    overAllWarnings: {
      code: "",
      description: "",
    },
    overAllRejections: {
      code: "",
      description: "",
    },
    overAllFundWarnings: {
      code: "",
      description: "",
    },
    overAllFundRejections: {
      code: "",
      description: "",
    },
    hnetNumber: "",
    mediKreditMsg: "",
    lineResults: [
      {
        lineNumber: "",
        status: "",
        warnings: "",
        rejections: "",
        tariffCode: "",
        grossAmount: "",
      },
    ],
    accountId: "",
    invoiceId: "",
    practiceId: "",
    patientId: "",
  });

  const { id } = useParams(); // Get the invoiceId from the URL parameters
  const location = useLocation();

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!invoice?._id || !account?._id) return null;
    const fi = (account.accountFullNames || '').charAt(0);
    const accountLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
    const invoiceLabel = `Invoice ${invoice.invoiceNumber}`;

    // Generic trail from source page (per D-06)
    const incomingTrail = location.state?.breadcrumbTrail;
    if (incomingTrail?.length) {
      return [
        ...incomingTrail,
        { label: invoiceLabel },
      ];
    }

    // Canonical fallback (per D-14) — same as existing default
    return [
      { label: 'Accounts', path: '/billing', canAccess: canAccessAccounts },
      { label: accountLabel, path: `/accounts/${account._id}`, canAccess: canAccessAccounts },
      { label: invoiceLabel },
    ];
  }, [invoice, account, location.state, canAccessAccounts]);

  useEffect(() => {
    const getInvoiceData = async () => {
      try {
        const token = sessionStorage.getItem("user");
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken._id);
        const response = await getInvoiceById(id); // Replace with your API endpoint
        setInvoice(response);
        console.log("Invoice data:", response);
        const accountData = await getAccountById(response.accountId); // Fetch account data using the accountId from the invoice
        setAccount(accountData);
        const patientData = await getPatientById(response.patient.patientId); // Fetch patient data using the patientId from the invoice
        setPatients(patientData);
        const practiceData = await getPracticeById(
          response.practice.practiceNumber
        ); // Fetch practice data using the practiceId from the invoice
        setPractice(practiceData);
        const practitionerData = await getUserById(response.practitioner.id); // Fetch practitioner data using the practitionerId from the invoice
        setPractitioner(practitionerData);
        const claimData = await getClaimByInvoiceId(id); // Fetch claim data using the invoiceId
        if (claimData) {
          setClaim(claimData);
          setClaimId(claimData._id);
        } else {
          console.log("No claim data found for this invoice.");
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
    };
    getInvoiceData();
  }, [id]);

  useEffect(() => {
    if (claim?._id) {
      setClaimId(claim?._id);
      console.log("claim updated:", claim);
    }
  }, [claim]);

  async function handlePrint() {
    if (invoice) {
      // Create base invoice data object
      const invoiceData = {
        ...invoice,
        account,
        practice,
        practitioner,
      };

      try {
        // Get raw banking data
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            })
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber
          );
          invoiceData.banking = [];
        }

        // Export the PDF with the complete data including decrypted banking details
        await ExportPDFInvoice(invoiceData);
        console.log("exportInvoiceData", invoiceData);
      } catch (error) {
        console.error("Error in handlePrint:", error);
        // Still try to export the PDF even if banking details failed
        await ExportPDFInvoice(invoiceData);
      }
    } else {
      console.warn("No invoice data available for printing");
    }
  }

  async function prepareEmailInvoice() {
    try {
      if (invoice) {
        const invoiceData = {
          ...invoice,
          account,
          practice,
          practitioner,
        };
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            })
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber
          );
          invoiceData.banking = [];
        }

        // Call ExportPDFInvoice with asBlob: true directly
        const blob = await ExportPDFInvoice(invoiceData, { asBlob: true });

        // Only continue if we got a blob back
        if (blob) {
          setStatementBlob(blob);
          setShowEmailModal(true);
        }
      }
    } catch (error) {
      console.error("❌ Failed to generate invoice PDF:", error);
    }
  }

  const handleSubmitClaim = async () => {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);

    try {
      console.log("Claim submission response:", invoice);
      console.log("Account data at submission:", account);
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: "",
      });
      console.log("Claim submission response:", invoice);
      const lineStatuses = (response.lineResults || []).map((l) =>
        (l?.status || "").toUpperCase()
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mediKreditStatus = (
            response.mediKreditStatus || ""
          ).toLowerCase();
          if (
            mediKreditStatus === "p" &&
            !response.overAllRejections &&
            !response.overAllFundRejections
          ) {
            computedStatus = "Processed - Item Level Warnings Or Rejections";
          } else {
            computedStatus = "Rejected";
          }
          if (mediKreditStatus === "p") {
            computedStatus = "Processed";
          }
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        // fallback if no lineResults
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
            ? "Switch Rejected"
            : code === "w"
            ? "Item Level Warnings Or Rejections"
            : "Pending";
      }

      const updatedClaim = {
        ...response,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };
      setInvoice(newInvoice);
      await updateInvoice(newInvoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
      navigate(0)
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      const errorMsg = err.response?.data?.error || err.message || "Unknown error";
      setErrorMessage(errorMsg);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  };

  const handleReceipt = () => {
    setShowReceiptModal(true);
  };

  const handleUpdateClaim = () => {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {
      // state: { initialInvoiceApp: invoice }, // ✅ pass full invoice
    });
  };

  async function handleUpdateInvoice() {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {});
  }

  async function handleResubmitClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: claim.transactionNumber,
      });

      const lineStatuses = (response.lineResults || []).map((l) =>
        (l.status || "").toUpperCase()
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mkStatus = (response.mediKreditStatus || "").toLowerCase();
          computedStatus =
            mkStatus === "p"
              ? "Processed - Item Level Warnings Or Rejections"
              : "Processed";
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
            ? "Switch Rejected"
            : code === "w"
            ? "Item Level Warnings Or Rejections"
            : "Pending";
      }

      const updatedClaim = {
        ...response,
        _id: claimId,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };

      // Save claim
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };

      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
      const errorMsg = err.response?.data?.error || err.message || "Unknown error";
      setErrorMessage(errorMsg);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleReverseClaim() {
    setLoadingClaim(true);
    try {
      const reverseClaim = await ReverseClaim({
        account,
        invoice,
        practice: practice,
        selectedPatient: patient,
        claimtxn: claim?.transactionNumber,
      });

      // Determine status based on MK status code
      const mediKreditStatus = (status) => {
        switch (status?.toLowerCase()) {
          case "p":
            return "Processed - Claim Reversed";
          case "r":
            return "Reversal Switch Rejected";
          case "w":
            return "Processed - Item Level Warnings Or Rejections";
          case "a":
            return "Approved - Claim Reversed";
          default:
            return "Pending";
        }
      };

      // Update local claim object
      const updatedClaimData = {
        ...reverseClaim,
        _id: claimId,
        lineResults: [],
        status: mediKreditStatus(reverseClaim?.mediKreditStatus),
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Reversed",
        userId: userId,
      };
      setClaim(updatedClaimData);

      // Update local invoice object
      const updatedInvoiceData = {
        ...invoice,
        status: "Claim In Progress",
      };

      setInvoice(updatedInvoiceData);

      // Persist both to DB
      await updateInvoice(invoice?._id, updatedInvoiceData);
      const updatedClaim = await updateClaim(claimId, updatedClaimData);

      setClaimId(updatedClaim.claimId);
    } catch (err) {
      console.error("🚨 Error in claim update:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleChangeLiability() {
    setLiabilityModalShow(true);
  }

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return "warning";
      case "pending":
        return "secondary";
      case "update posted":
        return "danger";
      case "posted":
        return "success";
      case "claim - processed":
        return "success";
      case "posted - claim in progress":
        return "secondary";
      case "paid":
        return "primary";
      case "claim in progress":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "dark";
    }
  };

  const getStatusText = (code) => {
    switch (code) {
      case "R":
        return "Rejected";
      case "P":
        return "Processed";
      case "W":
        return "Processed with Warnings";
      case "A":
        return "Approved";
      default:
        return code || "-";
    }
  };

  const getStatusVariantLines = (code, approvedAmount) => {
    // ✅ First handle the "danger" condition globally
    if (
      code === "R" ||
      approvedAmount == null ||
      approvedAmount === 0 ||
      approvedAmount === "0.00"
    ) {
      return "danger";
    }

    // ✅ Then fall back to your normal switch for other cases
    switch (code) {
      case "P":
        return "primary";
      case "W":
        return "secondary";
      case "A":
        return "success";
      default:
        return "muted";
    }
  };

  async function handleManualClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    try {
      const updatedClaim = {
        status: "Manual Submission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: "Posted - Manual Submission",
      };
      setInvoice(newInvoice);
      await updateInvoice(invoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
      setManualSubmit(true);
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleManualResubmit() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const updatedClaim = {
        _id: claimId,
        status: "Manual Resubmission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Posted")
          ? "Posted - Manual Submission"
          : "Posted - Manual Submission",
      };
      setManualSubmit(true);
      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
    } finally {
      setLoadingClaim(false);
    }
  }

  const STATUS_BUTTONS = {
    "Pending Submission": [
      { label: "Update Claim", onClick: handleUpdateClaim, icon: "fa-pen" },
      {
        label: "E-Submission",
        onClick: handleSubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Submission",
        onClick: handleManualClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Processed: [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Not Paid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Item Level Warnings Or Rejections": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Switch Rejected": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Manual Submission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Manual Resubmission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Approved - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Claim Updated": [
      {
        label: "E-Resubmission",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Resubmission",
        onClick: handleManualResubmit,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Rejected - Medical Aid": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Resubmit Claim",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
    ],
    "Processed - Medical Aid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Finalised: [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Finalised - Not Paid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Reversal Switch Rejected": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Paid: [{}],
  };

  const buttons = STATUS_BUTTONS[claim?.status] || [];

  const buttonRows = [];
  for (let i = 0; i < buttons.length; i += 2) {
    buttonRows.push([buttons[i], buttons[i + 1]]);
  }

  // Place these outside the return/render block
  async function calculateAndSaveLineAmounts(invoice, claim) {
    // Guard
    if (!invoice || !Array.isArray(invoice.lines)) {
      return { patientTotal: 0, medAidTotal: 0 };
    }
    if (!claim._id) {
      return {
        patientTotal: invoice.patientTotal,
        medAidTotal: invoice.medAidTotal,
      };
    }

    const round2 = (n) => Number((parseFloat(n ?? 0) || 0).toFixed(2));
    const cleanNumber = (v) =>
      round2(
        String(v ?? 0)
          .toString()
          .replace(/[, ]+/g, "")
      );

    // Step 1: update each line with patientAmount / medAidAmount
    // IOD accounts are treated the same as medical aid — amounts go to scheme, not patient
    const hasScheme = Boolean(invoice?.medicalAid?.name || account?.accountIODFund);

    invoice.lines = invoice.lines.map((line) => {
      const full = cleanNumber(line.rate);
      const claimLine = claim?.lineResults?.find(
        (c) => String(c.lineNumber) === String(line.lineNumber)
      );

      let medAidAmt = 0;
      let patientAmt = 0;

      if (!hasScheme || line.privatePayment) {
        // No scheme on account, or explicitly private → patient pays full
        patientAmt = full;
      } else if (claim.status === "Switch Rejected") {
        // Entire claim was rejected by switch — no scheme approval, patient pays full
        patientAmt = full;
      } else if (!claimLine) {
        // Scheme present but no claim feedback yet
        // If there is a billable code (tariff or nappi), allocate to scheme
        const hasBillableCode = Boolean(line.tariffCode || line.nappiCode);
        medAidAmt = hasBillableCode ? full : 0;
        patientAmt = hasBillableCode ? 0 : full;
      } else {
        // We have claim feedback: split by approved nett
        const approved = cleanNumber(claimLine.approvedNettAmount);
        // Clamp to avoid weird negatives/over-allocations
        medAidAmt = Math.max(0, Math.min(approved, full));
        patientAmt = round2(full - medAidAmt);
      }
      console.log("medAidAmt", medAidAmt, "patientAmt", patientAmt, "for line", line.lineNumber);

      return {
        ...line,
        patientAmount: round2(patientAmt),
        medAidAmount: round2(medAidAmt),
      };
      
    }
  
  );
    // Step 2: totals
    const totals = invoice.lines.reduce(
      (acc, line) => {
        acc.patientTotal = round2(acc.patientTotal + (line.patientAmount || 0));
        acc.medAidTotal = round2(acc.medAidTotal + (line.medAidAmount || 0));
        return acc;
      },
      { patientTotal: 0, medAidTotal: 0 }
     );
      console.log("Calculated totals from lines:", totals);

    // Step 3: receipts / credit-notes / refunds per bucket
    const sumPaymentsFor = (bucket) =>
      (invoice.receipts || []).reduce((sum, r) => {
        const src = String(r.paymentSource || "")
          .toLowerCase()
          .replace(/\s+/g, ""); // "medical aid" → "medicalaid"
        const match =
          bucket === "patient" ? src === "patient" : src === "medicalaid";
        if (!match) return sum;

        const receiptAmt = cleanNumber(r.receiptAmount);
        const creditAmt = cleanNumber(r.creditNoteAmount);
        const refundAmt = cleanNumber(r.refundAmount);

        // receipts + credits reduce outstanding; refunds increase it
        return round2(sum + receiptAmt + creditAmt - refundAmt);
      }, 0);

    const patientReceipts = sumPaymentsFor("patient");
    const medAidReceipts = sumPaymentsFor("medicalAid");

    // Step 4: outstanding & paid
    const patientOutstanding = round2(totals.patientTotal - patientReceipts);
    const medAidOutstanding = round2(totals.medAidTotal - medAidReceipts);
    const totalOutstanding = round2(patientOutstanding + medAidOutstanding);
    const paidAmount = round2(patientReceipts + medAidReceipts);

    // Step 5: write back to invoice
    invoice.patientTotal = totals.patientTotal;
    invoice.totalPatientAmount = totals.patientTotal;
    invoice.medAidTotal = totals.medAidTotal;
    invoice.totalMedicalAidAmount = totals.medAidTotal;
    invoice.outstandingBalance = {
      patientOutstanding,
      medAidOutstanding,
      totalOutstanding,
      paidAmount,
    };
    console.log("Updated invoice with totals and outstanding:", invoice);
    await updateInvoice(invoice?._id, invoice);
    console.log("Totals from invoice state:", {
    patientTotal: totals.patientTotal,
    medAidTotal: totals.medAidTotal,
  });
    return {
      patientTotal: totals.patientTotal,
      medAidTotal: totals.medAidTotal,
    };
  }
 
  useEffect(() => {
    if (invoice?._id && claim?.lineResults?.length > 0) {
      calculateAndSaveLineAmounts(invoice, claim)
        .then(({ patientTotal, medAidTotal }) => {
          setPatTotal(patientTotal);
          setMedTotal(medAidTotal);
        })
        .catch((err) => {
          console.error("🚨 Failed to update invoice:", err);
        });
    } else if (invoice?._id) {
      setPatTotal(invoice.patientTotal ?? invoice.totalPatientAmount ?? 0);
      setMedTotal(invoice.medAidTotal ?? invoice.totalMedicalAidAmount ?? 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, claim, account?.accountIODFund]);

  useEffect(() => {
    if (invoice?._id && Array.isArray(invoice.receipts)) {
      // initialize all six totals
      let totalPatientReceipts = 0;
      let totalMedicalAidReceipts = 0;
      let totalPatientCreditNotes = 0;
      let totalMedicalAidCreditNotes = 0;
      let totalPatientRefunds = 0;
      let totalMedicalAidRefunds = 0;

      invoice.receipts.forEach((r) => {
        // normalize source key to lower-case for consistency
        const src = (r.paymentSource || "").toString().toLowerCase();

        // 1) Receipts
        if (r.receiptAmount != null) {
          const amt = parseFloat(r.receiptAmount) || 0;
          if (src === "patient") totalPatientReceipts += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidReceipts += amt;
        }

        // 2) Credit-notes
        if (r.creditNoteAmount != null) {
          const amt = parseFloat(r.creditNoteAmount) || 0;
          if (src === "patient") totalPatientCreditNotes += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidCreditNotes += amt;
        }

        // 3) Refunds
        if (r.refundAmount != null) {
          const amt = parseFloat(r.refundAmount) || 0;
          if (src === "patient") totalPatientRefunds += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidRefunds += amt;
        }
      });

      // set them all into state
      setPatReceiptTotal(totalPatientReceipts);
      setMedReceiptTotal(totalMedicalAidReceipts);
      setPatCreditTotal(totalPatientCreditNotes);
      setMedCreditTotal(totalMedicalAidCreditNotes);
      setPatRefundTotal(totalPatientRefunds);
      setMedRefundTotal(totalMedicalAidRefunds);
    }
  }, [invoice]);

  const F = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00"; // fallback

    return num.toFixed(2); // ALWAYS uses "." regardless of locale
  };

  const ICD10Display = ({ oti, primaryCode, secondaryCodes = [] }) => {
    // Function to chunk array into groups of 2
    const chunkArray = (array, chunkSize) => {
      const chunks = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    };

    const secondaryChunks = chunkArray(secondaryCodes, 2);

    return (
      <div className="text-center">
        {/* Primary ICD10 Code */}
        <div className="fw-semibold text-center mb-1 d-flex justify-content-center align-items-center gap-2">
          {oti === "PB" && (
            <span className="badge bg-success text-white small px-2 py-1">
              PMB
            </span>
          )}
          <span>{primaryCode}</span>
        </div>

        {/* Secondary ICD10 Codes */}
        {secondaryChunks.map((chunk, index) => (
          <div key={index} className="text-center mb-1">
            {chunk.map((code, codeIndex) => (
              <span key={codeIndex} className="me-1">
                {code}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const prettyDT = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return String(iso);

    const pad = (n) => String(n).padStart(2, "0");

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1); // months are 0-based
    const year = String(d.getFullYear()).slice(-2); // last 2 digits
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  return (
    <div>
      {manualSubmit && (
        <Modal show={manualSubmit} onHide={() => setManualSubmit(false)}>
          <Modal.Header>
            <Modal.Title className="text-primary">
              Manual Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You have marked this claim as manually submitted. There will be no
              action from PractoSync, and you will need to follow up with the
              medical aid directly.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setManualSubmit(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {breadcrumbItems && (
        <div style={{ marginTop: "65px" }} className="ms-3 me-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      )}
      <Container
        fluid
        className="bg-white border-bottom pt-1 pb-1"
        style={{ marginTop: breadcrumbItems ? "0" : "65px" }}
      >
        <Row className="align-items-center justify-content-start ms-3 mb-1 gx-0">
          <Col md={6} className="ps-0 pe-0">
            <h6 className="text-primary text-start mb-1 fw-normal">
              INVOICE &amp; CLAIM
            </h6>
          </Col>

          <Col
            md={6}
            className="d-flex align-items-center justify-content-end gap-2"
          >
            <h6
              className={`mb-0 fw-semibold me-4 text-${getStatusVariant(
                invoice?.status
              )}`}
            >
              {invoice?.status}
            </h6>

            {/* === Uniform Buttons Section === */}
            <div
              className="d-flex align-items-center justify-content-end gap-2"
              style={{ minWidth: "390px" }}
            >
              {/* Actions Dropdown */}
              <Dropdown className="flex-grow-1">
                <Dropdown.Toggle
                  variant="primary"
                  size="lg"
                  className="rounded-0 uniform-btn"
                  id="actions-dropdown"
                >
                  <i className="fa-solid fa-gears me-1" />
                  Actions
                </Dropdown.Toggle>

                <Dropdown.Menu
                  align="end"
                  className="shadow-sm actions-dropdown"
                >
                  {/* Paid → Only Update Invoice */}
                  {invoice?.status === "Paid" && (
                    <Dropdown.Item onClick={handleUpdateInvoice}>
                      <i className="fa fa-edit me-2"></i>
                      Update Invoice
                    </Dropdown.Item>
                  )}

                  {/* Posted → Receive Payment + Update Invoice */}
                  {invoice?.status === "Posted" && (
                    <>
                      <Dropdown.Item onClick={handleReceipt}>
                        <i className="fa fa-receipt me-2"></i>
                        Receive Payment
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleUpdateInvoice}>
                        <i className="fa fa-edit me-2"></i>
                        Update Invoice
                      </Dropdown.Item>
                    </>
                  )}

                  {/* Other statuses → Claim actions */}
                  {invoice?.status !== "Paid" &&
                    invoice?.status !== "Posted" &&
                    STATUS_BUTTONS[claim?.status]?.length > 0 && (
                      <>
                        {STATUS_BUTTONS[claim?.status].map(
                          (button, index) =>
                            button.label && (
                              <Dropdown.Item
                                key={index}
                                onClick={button.onClick}
                              >
                                <i className={`fa ${button.icon} me-2`}></i>
                                {button.label}
                              </Dropdown.Item>
                            )
                        )}
                      </>
                    )}
                </Dropdown.Menu>
              </Dropdown>

              {/* Email */}
              <Button
                variant="secondary"
                size="lg"
                className="rounded-0 uniform-btn"
                onClick={prepareEmailInvoice}
              >
                <i className="fa-regular fa-envelope me-1" />
                Email
              </Button>

              {/* Download */}
              <Button
                variant="secondary"
                size="lg"
                className="rounded-0 uniform-btn"
                onClick={handlePrint}
              >
                <i className="fa fa-download me-1" />
                Download
              </Button>
            </div>
          </Col>
        </Row>
      </Container>

      <Row className="ms-2 me-2 gx-2 mt-1">
        {/* Left: Invoice header + lines */}
        <Col md={9}>
          {/* --- Invoice Header --- */}
          <Container className="border-end p-1 mb-3">
            <Row
              className="fw-normal text-primary mb-2 ms-1 border-bottom border-top border-primary me-1 py-1"
              style={{ backgroundColor: "rgb(206, 226, 254)" }}
            >
              <Col md={3} className="text-start fw-bold">
                Account
              </Col>
              <Col md={3} className="text-start fw-bold">
                Patient
              </Col>
              <Col md={3} className="text-start fw-bold">
                Practice &amp; Practitioner
              </Col>
              <Col md={3} className="text-start fw-bold">
                Invoice Info
              </Col>
            </Row>

            <Row className="gx-3 mt-2 ms-1">
              {/* --- Account --- */}
              <Col md={3} className="small">
                <p className="fw-semibold text-primary mb-1">
                  {account?.accountTitle} {account?.accountFullNames}{" "}
                  {account?.accountLastName}
                </p>
                <p className="mb-0">{account?.accountHomeAddress1}</p>
                <p className="mb-0">{account?.accountHomeAddress2}</p>
                <p className="mb-0">
                  {account?.accountHomeAddressCity}{" "}
                  {account?.accountHomeAddressPostalCode}
                </p>
                <p className="mb-0">{account?.accountTelNumberCell}</p>
                <p className="mb-0">{account?.accountEmail}</p>
              </Col>

              {/* --- Patient --- */}
              <Col md={3} className="small">
                <p className="fw-semibold text-primary mb-1">
                  {patient?.patientTitle} {patient?.patientFullNames} (
                  {patient?.patientInitials}) {patient?.patientLastName}
                </p>
                {account.accountMedicalAid ? (
                  <>
                    <p className="mb-0 d-flex align-items-center gap-2">
                      <span className="badge bg-primary">Medical Aid</span>
                      <span>{account.accountMedicalAid}</span>
                    </p>

                    {(account.accountMedicalAidPlan ||
                      account.accountMedicalAidOption) && (
                      <p className="mb-0">
                        {account.accountMedicalAidPlan}
                        {account.accountMedicalAidPlan &&
                          account.accountMedicalAidOption &&
                          " | "}
                        {account.accountMedicalAidOption}
                      </p>
                    )}

                    <p className="mb-0">
                      {account.accountMedicalAidNumber}
                      {patient?.patientMedicalAidDependantCode
                        ? ` (${patient.patientMedicalAidDependantCode})`
                        : ""}
                    </p>

                    {patient?.patientMedicalAidStatus && (
                      <p
                        className={`fw-bold mb-0 ${
                          ["Suspended", "Resigned"].includes(
                            patient.patientMedicalAidStatus
                          )
                            ? "text-warning"
                            : "text-success"
                        }`}
                      >
                        {patient.patientMedicalAidStatus}
                      </p>
                    )}
                  </>
                ) : account.accountIODFund ? (
                  <>
                    <p className="mb-0 d-flex align-items-center gap-2">
                      <span className="badge bg-warning text-dark">IOD</span>
                      <span>{account.accountIODFund}</span>
                    </p>

                    {(account.accountIODPlan || account.accountIODOption) && (
                      <p className="mb-0">
                        {account.accountIODPlan}
                        {account.accountIODPlan &&
                          account.accountIODOption &&
                          " | "}
                        {account.accountIODOption}
                      </p>
                    )}

                    {account.accountIODClaimRefNumber && (
                      <p className="mb-0">
                        Claim Ref: {account.accountIODClaimRefNumber}
                      </p>
                    )}

                    {account.accountIODEmployer && (
                      <p className="mb-0">
                        Employer: {account.accountIODEmployer}
                      </p>
                    )}

                    {account.accountIODEmployerWCARegNum && (
                      <p className="mb-0">
                        WCA Reg #: {account.accountIODEmployerWCARegNum}
                      </p>
                    )}
                    {account.accountIODDateOfInjury && (
                      <p className="mb-0">
                        Date of Injury: {account.accountIODDateOfInjury}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mb-0">
                    <span className="badge bg-secondary">Private Patient</span>
                  </p>
                )}
              </Col>

              {/* --- Practice & Practitioner --- */}
              <Col md={3} className="small">
                <p className="fw-semibold text-primary mb-1 text-uppercase">
                  {invoice?.practice?.practiceName}
                </p>
                <p className="mb-0">BHF: {invoice?.practice?.practiceNumber}</p>
                <p className="mb-0">
                  VAT: {invoice?.practice?.vatNumber || "-"}
                </p>
                <p className="mb-0 text-uppercase mt-1">
                  {invoice?.practitioner?.firstName}{" "}
                  {invoice?.practitioner?.lastName}
                </p>
                <p className="mb-0">HPCSA: {invoice?.practitioner?.hpcsa}</p>
              </Col>

              {/* --- Invoice Info --- */}
              <Col md={3} className="small">
                <Row className="mb-1">
                  <Col xs={3} className="fw-semibold text-primary text-start">
                    Invoice:
                  </Col>
                  <Col xs={6} className="text-start">
                    {invoice?.invoiceNumber}
                  </Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={3} className="fw-semibold text-primary text-start">
                    Date:
                  </Col>
                  <Col xs={6} className="text-start">
                    {invoice?.invoiceDate}
                  </Col>
                </Row>
                <Row className="mb-1">
                  <Col xs={3} className="fw-semibold text-primary text-start">
                    Status:
                  </Col>
                  <Col xs={7} className="text-start">
                    {invoice?.status}
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row
              className="fw-normal text-primary mb-1 mt-2 ms-1 border-bottom border-top border-primary me-2"
              style={{ backgroundColor: "rgb(206.6, 226, 254.6)" }}
            >
              <Col md={2} className="text-center fw-bold">
                Date
              </Col>
              <Col md={1} className="text-center fw-bold">
                ICD10
              </Col>
              <Col md={1} className="text-center fw-bold">
                Code | (Modifier)
              </Col>
              <Col md={4} className="text-center fw-bold">
                Description
              </Col>
              <Col md={1} className="text-center fw-bold">
                Unit(s)
              </Col>
              <Col md={1} className="text-center fw-bold">
                Patient
              </Col>
              <Col md={1} className="text-center fw-bold">
                Med Aid
              </Col>
              <Col md={1} className="text-center fw-bold">
                Total
              </Col>
            </Row>

            {/* --- Line Items --- */}

            <>
              {invoice?.lines?.map((line, i) => {
                const claimLine = claim?.lineResults?.find(
                  (c) => String(c.lineNumber) === String(line.lineNumber)
                );

                const full = parseFloat(line.rate) || 0;
                const hasScheme = !!(invoice.medicalAid?.name || account?.accountIODFund);

                let medAidAmt = 0;
                let patientAmt = 0;
                const totalAmt = full;

                if (!hasScheme || line.privatePayment) {
                  patientAmt = full;
                } else if (claim?.status === "Switch Rejected") {
                  // Entire claim rejected by switch — no scheme approval, patient pays full
                  patientAmt = full;
                } else if (hasScheme && !claimLine) {
                  if (line.tariffCode || line.nappiCode) {
                    medAidAmt = full;
                  } else {
                    patientAmt = full;
                  }
                } else {
                  const approved = parseFloat(
                    claimLine?.approvedNettAmount ?? 0
                  );
                  medAidAmt = approved;
                  if (approved !== full) {
                    patientAmt = full - approved;
                  }
                }
                if (!claimLine && line.patientAmount) {
                  patientAmt = parseFloat(line.patientAmount) || 0;
                  medAidAmt = full - patientAmt;
                }

                return (
                  <React.Fragment key={i}>
                    {claimLine && (
                      <>
                        <Row className="mb-1 ms-1 me-2 align-items-center small">
                          <Col
                            md={3}
                            className={`fw-bold text-start text-${getStatusVariantLines(
                              claimLine?.status,
                              claimLine?.approvedNettAmount
                            )}`}
                          >
                            {getStatusText(claimLine.status)}
                          </Col>

                          <Col
                            md={4}
                            className={`fw-bold text-end text-${getStatusVariantLines(
                              claimLine?.status,
                              claimLine?.approvedNettAmount
                            )}`}
                          ></Col>

                          <Col
                            md={4}
                            className={`fw-bold text-end text-${getStatusVariantLines(
                              claimLine?.status,
                              claimLine?.approvedNettAmount
                            )}`}
                          >
                            <div className="d-flex align-items-center justify-content-end">
                              <span className="me-2">
                                Approved Nett Amount:
                              </span>
                              <span>
                                {claimLine?.approvedNettAmount != null
                                  ? F(Number(claimLine?.approvedNettAmount))
                                  : "R 0.00"}
                              </span>
                            </div>
                          </Col>
                          <Col md={1} className="fw-bold text-end">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2"
                              onClick={() => {
                                setSelectedClaimLine(claimLine);
                                setShowLineInfo(true);
                              }}
                            >
                              <i
                                className={`fa fa-info-circle text-${getStatusVariantLines(
                                  claimLine?.status,
                                  claimLine?.approvedNettAmount
                                )}`}
                              ></i>
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row className="mb-1 ms-1 border-bottom border-gray me-2 small">
                      <Col md={2} className="text-center">
                        {line.appointmentCustomStart ? (
                          <>
                            <div>{prettyDT(line.appointmentCustomStart)}</div>
                            <div>to</div>
                            <div>{prettyDT(line.appointmentCustomEnd)}</div>
                          </>
                        ) : line.appointmentStartSet ? (
                          <>
                            <div>{prettyDT(line.appointmentStartSet)}</div>
                            <div>to</div>
                            <div>{prettyDT(line.appointmentEndSet)}</div>
                          </>
                        ) : (
                          <div>
                            {line.appointmentDateCustom ||
                              line.appointmentDateSet ||
                              "-"}
                          </div>
                        )}
                      </Col>

                      <Col md={1} className="text-center">
                        <ICD10Display
                          oti={line.oti}
                          primaryCode={line.icd101}
                          secondaryCodes={
                            line.icd102
                              ? line.icd102
                                  .split(";")
                                  .map((code) => code.trim())
                                  .filter(Boolean)
                              : []
                          }
                        />
                      </Col>
                      <Col md={1} className="text-center">
                        {(() => {
                          // If it's a NAPPI line (either explicitly typed or has a code), show nappiCode.
                          const isNappi =
                            line.type === "nappi" || !!line.nappiCode;
                          if (isNappi) return line.nappiCode || "-";

                          // Otherwise show tariff with modifiers inline.
                          const mods = Array.isArray(line.modifier)
                            ? line.modifier
                                .map((m) =>
                                  typeof m === "string" ? m : m?.code
                                )
                                .filter(Boolean)
                            : [];

                          if (!line.tariffCode) return "-";
                          return mods.length
                            ? `${line.tariffCode} (${mods.join(", ")})`
                            : line.tariffCode;
                        })()}
                      </Col>

                      <Col md={4} className="text-start">
                        <span
                          title={(() => {
                            const desc = line.description || "";
                            const spName = line.serviceProvider
                              ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`
                              : invoice?.practitioner
                              ? `${invoice.practitioner.firstName} ${invoice.practitioner.lastName}`
                              : "";
                            return spName ? `${desc}\nService Provider: ${spName}` : desc;
                          })()}
                          style={{ cursor: "help" }}
                        >
                          {line.description || "-"}
                        </span>
                      </Col>
                      <Col md={1} className="text-center">
                        {line.quantity || "-"}
                      </Col>
                      <Col md={1} className="text-end">
                        R {F(patientAmt)}
                      </Col>
                      <Col md={1} className="text-end">
                        R {F(medAidAmt)}
                      </Col>
                      <Col md={1} className="text-end fw-bold">
                        R {(totalAmt ?? 0).toFixed(2)}
                      </Col>
                    </Row>
                  </React.Fragment>
                );
              })}
              <Modal
                show={showLineInfo}
                onHide={() => setShowLineInfo(false)}
                size="lg"
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Line details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Row className="mb-1 me-2">
                    <p className="mb-0 fw-bold text-primary">
                      Line Number: {selectedClaimLine?.lineNumber}
                    </p>
                  </Row>

                  <p className="mb-0 text-primary border-bottom border-primary pb-2">
                    {selectedClaimLine?.tariffCode} -{" "}
                    {selectedClaimLine?.description}
                  </p>

                  <Row className="mb-1 me-2 mt-2 align-items-center">
                    <Col
                      md={4}
                      className={`fw-bold text-start text-${getStatusVariantLines(
                        selectedClaimLine?.status
                      )}`}
                    >
                      <h6>{getStatusText(selectedClaimLine?.status)}</h6>
                    </Col>
                  </Row>

                  <Col
                    md={5}
                    className={`text-start text-${getStatusVariantLines(
                      selectedClaimLine?.status
                    )}`}
                  >
                    {selectedClaimLine?.warnings?.map((warn, index) => (
                      <p className="mb-0" key={`warn-${index}`}>
                        {warn.code} - {warn.description}
                      </p>
                    ))}

                    {selectedClaimLine?.rejections?.map((rej, index) => (
                      <p className="mb-0" key={`rej-${index}`}>
                        {rej.code} - {rej.description}
                      </p>
                    ))}
                  </Col>

                  <Col md={3}></Col>

                  <hr />

                  <dl className="row">
                    <dt className="col-6">Submitted Amount</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.submittedGrossAmount
                        ? `R ${Number(
                            selectedClaimLine.submittedGrossAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6">Approved Gross</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.approvedGrossAmount
                        ? `R ${Number(
                            selectedClaimLine.approvedGrossAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Discount</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedDiscount
                        ? `R ${Number(
                            selectedClaimLine.approvedDiscount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Copay</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedCopay
                        ? `R ${Number(selectedClaimLine.approvedCopay).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-normal">Approved Levy</dt>
                    <dd className="col-6 text-end">
                      {selectedClaimLine?.approvedLevy
                        ? `R ${Number(selectedClaimLine.approvedLevy).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6">Approved Net</dt>
                    <dd className="col-6 text-end border-top fw-bold">
                      {selectedClaimLine?.approvedNettAmount
                        ? `R ${Number(
                            selectedClaimLine.approvedNettAmount
                          ).toFixed(2)}`
                        : "R 0.00"}
                    </dd>
                    <dt className="col-6 fw-bold">Over-charged</dt>
                    <dd className="col-6 text-end fw-bold">
                      {selectedClaimLine?.overCharged
                        ? `R ${Number(selectedClaimLine.overCharged).toFixed(
                            2
                          )}`
                        : "R 0.00"}
                    </dd>
                  </dl>

                  {/* ⬇️ Modifier Details */}
                  {Array.isArray(selectedClaimLine?.modifiers) &&
                    selectedClaimLine.modifiers.length > 0 && (
                      <>
                        <hr />
                        {selectedClaimLine.modifiers.map((mod, idx) => (
                          <div key={`mod-${idx}`} className="ms-3 mb-3">
                            <h6 className="text-primary">
                              Modifier:{" "}
                              {mod.modifierCode || mod.modifier || "Unknown"}
                            </h6>
                            <p
                              className={`ms-2 mb-1 fw-bold text-${getStatusVariantLines(
                                mod.status
                              )}`}
                            >
                              Status: {getStatusText(mod.status)}
                            </p>

                            {mod.warnings?.length > 0 && (
                              <div>
                                {mod.warnings.map((warn, wIdx) => (
                                  <p
                                    className="ms-2 mb-0 text-secondary"
                                    key={`mod-warn-${wIdx}`}
                                  >
                                    {warn.code} - {warn.description}
                                  </p>
                                ))}
                              </div>
                            )}

                            {mod.rejections?.length > 0 && (
                              <div>
                                {mod.rejections.map((rej, rIdx) => (
                                  <p
                                    className="ms-2 mb-0 text-danger"
                                    key={`mod-rej-${rIdx}`}
                                  >
                                    {rej.code} - {rej.description}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowLineInfo(false)}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>

              <Row className="text-end ms-4 mt-2 me-2">
                <Col md={7}></Col>
                <Col md={5}>
                  <Row className="align-items-end my-1 border-bottom border-1 border-primary">
                    <Col xs={9}>
                      <p className="mb-1 fw-bold text-primary">
                        Patient Total:
                      </p>
                    </Col>
                    <Col xs={3}>
                      <p className="mb-1 fw-bold text-primary text-end">
                        R {F(patTotal)}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="text-end ms-4 mt-2 me-2">
                <Col md={7}></Col>
                <Col md={5}>
                  <Row className="align-items-end my-1 border-bottom border-1 border-primary">
                    <Col xs={9}>
                      <p className="mb-1 fw-bold text-primary">
                        Medical Aid Total:
                      </p>
                    </Col>
                    <Col xs={3}>
                      <p className="mb-1 fw-bold text-primary text-end">
                        R {F(medTotal)}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>

            {/* Invoice Total */}
            <Row className="text-end ms-4 mt-1 me-2">
              <Col md={7}></Col>
              <Col md={5}>
                <Row className="align-items-end my-1 border-bottom border-4 border-primary">
                  <Col xs={9}>
                    <p className="mb-1 fw-bold text-primary">Invoice Total:</p>
                  </Col>
                  <Col xs={3}>
                    <p className="mb-1 fw-bold text-primary text-end">
                      R {F(invoice?.totalAmount)}
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        <Col md={3}>
          <Accordion className="mt-0">
            {/* === RECEIPTS === */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <Col xs={4} className="fw-semibold">
                  <i className="fa-solid fa-money-bill-wave text-primary me-2"></i>
                  Due
                </Col>
                <Col xs={6} className="text-primary text-end">
                  R {F(invoice?.outstandingBalance?.totalOutstanding)}
                </Col>
              </Accordion.Header>
              <Accordion.Body>
                {invoice?.receipts !== null ? (
                  invoice?.status === "Paid" ||
                  invoice?.status === "Partially Paid" ? (
                    <>
                      <Row className="gx-0 mb-1">
                        <Col xs={6} className="fw-normal text-primary">
                          Invoice Amount:
                        </Col>
                        <Col xs={6} className="text-primary text-end fw-normal">
                          R {F(invoice?.totalAmount)}
                        </Col>
                      </Row>

                      {patReceiptTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Paid by Patient:
                          </Col>
                          <Col
                            xs={6}
                            className="text-primary text-end fw-normal"
                          >
                            (R {F(patReceiptTotal)})
                          </Col>
                        </Row>
                      )}
                      {medReceiptTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Paid by Medical Aid:
                          </Col>
                          <Col
                            xs={6}
                            className="text-primary text-end fw-normal"
                          >
                            (R {F(medReceiptTotal)})
                          </Col>
                        </Row>
                      )}
                      {patCreditTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Credit Note to Patient:
                          </Col>
                          <Col xs={6} className="text-primary text-end">
                            (R {F(patCreditTotal)})
                          </Col>
                        </Row>
                      )}
                      {medCreditTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Credit Note to Medical Aid:
                          </Col>
                          <Col xs={6} className="text-primary text-end">
                            (R {F(medCreditTotal)})
                          </Col>
                        </Row>
                      )}
                      {patRefundTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Refund to Patient:
                          </Col>
                          <Col xs={6} className="text-primary text-end">
                            R {patRefundTotal.toFixed(2)}
                          </Col>
                        </Row>
                      )}
                      {medRefundTotal > 0 && (
                        <Row className="gx-0 mb-1">
                          <Col xs={6} className="fw-normal text-primary">
                            Refund to Medical Aid:
                          </Col>
                          <Col xs={6} className="text-primary text-end">
                            R {medRefundTotal.toFixed(2)}
                          </Col>
                        </Row>
                      )}

                      <Row className="gx-0 mb-1 mt-2 border-top border-2 border-primary mb-3">
                        <Col xs={6} className="fw-bold text-primary">
                          Total Outstanding:
                        </Col>
                        <Col xs={6} className="text-primary text-end fw-bold">
                          R {F(invoice?.outstandingBalance?.totalOutstanding)}
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Row>
                      <Col className="text-muted small fst-italic text-center">
                        No paid / partial payments.
                      </Col>
                    </Row>
                  )
                ) : (
                  <Row>
                    <Col className="text-muted small fst-italic text-center">
                      No receipts / credits / refunds.
                    </Col>
                  </Row>
                )}
              </Accordion.Body>
            </Accordion.Item>

            {/* === CASE === */}
            {(invoice?.case || invoice?.inPatientCase) &&
              invoice?.medicalAid?.name && (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <Col
                      xs={4}
                      className="fw-semibold d-flex align-items-start"
                    >
                      <i className="fa-solid fa-folder-open text-primary me-2"></i>
                      Case
                    </Col>
                  </Accordion.Header>
                  <Accordion.Body>
                    {(() => {
                      const cRaw = invoice?.case ?? {};
                      const legacy = invoice?.inPatientCase ?? {};
                      const c = {
                        description:
                          cRaw.description ?? cRaw.caseDescription ?? "—",
                        referringDoctorName: cRaw.referringDoctorName ?? "—",
                        referringDoctorBHF:
                          cRaw.referringDoctorBHF ?? legacy.caseBHF ?? "—",
                        referringDoctorHPCSA:
                          cRaw.referringDoctorHPCSA ?? legacy.caseHPCSA ?? "—",
                        authorizedBy: cRaw.authorizedBy ?? "—",
                        authorizationNumber:
                          cRaw.authorizationNumber ??
                          legacy.caseAuthNumber ??
                          "—",
                        startDate: cRaw.startDate ?? cRaw.caseStartDate ?? "—",
                        endDate: cRaw.endDate ?? cRaw.caseEndDate ?? "—",
                        icd101: cRaw.icd101 ?? legacy.caseICD10 ?? "",
                        icd102: Array.isArray(cRaw.icd102) ? cRaw.icd102 : [],
                      };
                      const icdPrimary = c.icd101 || "—";
                      const icdSecondary = c.icd102.length
                        ? c.icd102.join(", ")
                        : "—";

                      return (
                        <>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Description:
                            </Col>
                            <Col xs={6}>{c.description}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Doctor:
                            </Col>
                            <Col xs={6}>{c.referringDoctorName}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Ref BHF:
                            </Col>
                            <Col xs={6}>{c.referringDoctorBHF}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Ref HPCSA:
                            </Col>
                            <Col xs={6}>{c.referringDoctorHPCSA}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Authorized By:
                            </Col>
                            <Col xs={6}>{c.authorizedBy}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Authorization #:
                            </Col>
                            <Col xs={6}>{c.authorizationNumber}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Start Date:
                            </Col>
                            <Col xs={6}>{c.startDate}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              End Date:
                            </Col>
                            <Col xs={6}>{c.endDate}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              ICD-10 (Primary):
                            </Col>
                            <Col xs={6}>{icdPrimary}</Col>
                          </Row>
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              ICD-10 (Secondary):
                            </Col>
                            <Col xs={6}>{icdSecondary}</Col>
                          </Row>
                        </>
                      );
                    })()}
                  </Accordion.Body>
                </Accordion.Item>
              )}
            {/* === CLAIM STATUS === */}
            {(invoice?.medicalAid?.name || account?.accountIODFund) && (
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <Col xs={4} className="fw-semibold d-flex align-items-center">
                    <i
                      className={`fa-solid fa-file-medical me-2 ${
                        isServiceUnavailable ? "text-danger" : "text-primary"
                      }`}
                    ></i>
                    <span
                      className={
                        isServiceUnavailable ? "text-danger" : "text-primary"
                      }
                    >
                      Claim Status
                    </span>
                  </Col>

                  <Col xs={6} className="text-end">
                    {/* Show spinner while loading */}
                    {loadingClaim ? (
                      <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        variant={isServiceUnavailable ? "danger" : "primary"}
                        className="me-2"
                      >
                        <span className="visually-hidden">Loading…</span>
                      </Spinner>
                    ) : (
                      <span
                        className={`${
                          isServiceUnavailable ? "text-danger" : "text-primary"
                        }`}
                      >
                        {claim?.status}
                      </span>
                    )}
                  </Col>
                </Accordion.Header>
                <Accordion.Body>
                  <div id="claim-body">
                    {/* 1) Service unavailable? */}
                    {isServiceUnavailable ? (
                      <Row className="mb-3">
                        <Col>
                          <Alert variant="danger" className="text-center mb-0">
                            {errorMessage || "Service Unavailable"}
                          </Alert>
                        </Col>
                      </Row>
                    ) : loadingClaim ? (
                      <div className="d-flex justify-content-center py-3">
                        <Spinner
                          animation="border"
                          role="status"
                          variant="primary"
                        >
                          <span className="visually-hidden">Loading…</span>
                        </Spinner>
                      </div>
                    ) : (
                      <>
                        {/* Transaction & HNET */}
                        <Row className="mb-1">
                          <Col xs={6} className="text-primary">
                            Transaction #:
                          </Col>
                          <Col xs={6}>{claim?.transactionNumber || "-"}</Col>
                        </Row>
                        <Row className="mb-1">
                          <Col xs={6} className="text-primary">
                            HNET #:
                          </Col>
                          <Col xs={6}>{claim?.hnetNumber || "-"}</Col>
                        </Row>

                        {/* Messages */}
                        <Row className="text-primary mt-1 mb-1 ms-1">
                          {claim?.message}
                        </Row>

                        {/* Switch rejection */}
                        {claim?.overAllRejections?.description && (
                          <Row className="gx-0 mt-2 mb-1">
                            <Col xs={12} className="fw-bold text-danger">
                              Switch Rejection
                            </Col>
                            <Col xs={12} className="text-danger">
                              {claim.overAllRejections.description}
                            </Col>
                          </Row>
                        )}

                        {/* Medical Aid rejection */}
                        {claim?.overAllFundRejections?.description && (
                          <Row className="gx-0 mt-2 mb-1">
                            <Col xs={12} className="fw-bold text-danger">
                              Rejection
                            </Col>
                            <Col xs={12} className="text-danger">
                              {claim?.overAllFundRejections?.description}
                            </Col>
                          </Row>
                        )}

                        {/* Switch warning */}
                        {claim?.overAllWarnings?.description && (
                          <Row className="gx-0 mt-2 mb-1">
                            <Col xs={12} className="fw-bold text-secondary">
                              Messages / Warning
                            </Col>
                            <Col xs={12} className="text-secondary">
                              {claim.overAllWarnings.description}
                            </Col>
                          </Row>
                        )}

                        {/* Medical Aid warning */}
                        {claim?.overAllFundWarnings?.description && (
                          <Row className="gx-0 mt-2 mb-1">
                            <Col xs={12} className="fw-bold text-secondary">
                              Messages / Warning
                            </Col>
                            <Col xs={12} className="text-secondary">
                              {claim?.overAllFundWarnings?.description}
                            </Col>
                          </Row>
                        )}

                        {claim?.mediKreditMsg && (
                          <>
                            <Row className="text-secondary fw-bold mt-2 mb-1">
                              <Col>Message from the switch</Col>
                            </Row>
                            <Row className="text-secondary fw-bold">
                              {claim?.mediKreditMsg}
                            </Row>
                          </>
                        )}

                        {/* Financials */}
                        {claim?.financials?.approvedGross != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Approved Gross:
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim.financials.approvedGross)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.copay != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Copay:
                            </Col>
                            <Col xs={6} className="text-danger text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim.financials.copay)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.levy != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Levy:
                            </Col>
                            <Col xs={6} className="text-danger text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim.financials.levy)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.discount != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Discount:
                            </Col>
                            <Col xs={6} className="text-danger text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim?.financials.discount)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.approvedNett != null && (
                          <Row className="gx-0 mb-1 pt-1 border-top border-primary border-2">
                            <Col xs={6} className="fw-bold text-primary">
                              Approved Nett:
                            </Col>
                            <Col
                              xs={6}
                              className="text-primary fw-bold text-end"
                            >
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim?.financials.approvedNett)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.submittedAmount != null && (
                          <Row className="gx-0 mb-1 pb-1 ">
                            <Col xs={6} className="fw-bold text-primary">
                              Submitted Amount:
                            </Col>
                            <Col
                              xs={6}
                              className="text-primary fw-bold text-end"
                            >
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim?.financials.submittedAmount)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.surcharges?.schm != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Surcharged:
                            </Col>
                            <Col xs={6} className="text-danger text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim?.financials.surcharges.schm)}
                            </Col>
                          </Row>
                        )}
                        {claim?.financials?.surcharges?.save != null && (
                          <Row className="gx-0 mb-1">
                            <Col xs={6} className="fw-normal text-primary">
                              Surcharge paid from savings:
                            </Col>
                            <Col xs={6} className="text-danger text-end">
                              {new Intl.NumberFormat("en-ZA", {
                                style: "currency",
                                currency: "ZAR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(claim?.financials.surcharges.save)}
                            </Col>
                          </Row>
                        )}

                        {(claim?.patientClaimFinancials
                          ?.claimFinancialDescription ||
                          claim?.patientClaimFinancials
                            ?.claimFinancialAmount) && (
                          <>
                            <Row className="gx-0 mt-3 mb-1 ms-1 border-bottom border-primary">
                              <Col xs={6} className="fw-bold text-primary">
                                Patient Financials:
                              </Col>
                            </Row>
                            {claim?.patientClaimFinancials
                              .claimFinancialDescription && (
                              <Row className="gx-0 mb-1">
                                <Col xs={6} className="fw-normal text-primary">
                                  Description:
                                </Col>
                                <Col xs={6} className="text-end">
                                  {
                                    claim?.patientClaimFinancials
                                      ?.claimFinancialDescription
                                  }
                                </Col>
                              </Row>
                            )}
                            {claim?.patientClaimFinancials
                              .claimFinancialAmount && (
                              <Row className="gx-0 mb-1 border-bottom border-primary border-1">
                                <Col xs={6} className="fw-normal text-primary">
                                  Amount:
                                </Col>
                                <Col xs={6} className="text-end ">
                                  {new Intl.NumberFormat("en-ZA", {
                                    style: "currency",
                                    currency: "ZAR",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    claim?.patientClaimFinancials
                                      ?.claimFinancialAmount
                                  )}
                                </Col>
                              </Row>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            )}
          </Accordion>
        </Col>
      </Row>

      {showReceiptModal && account && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={account}
        />
      )}
      {showEmailModal && statementBlob && (
  <EmailInvoiceModal
    show={showEmailModal}
    onHide={() => setShowEmailModal(false)}
    defaultEmail={account.accountEmail}
    email2={account.accountEmail2}
    email3={account.accountEmail3}
    email4={account.accountEmail4}
    email5={account.accountEmail5}
    onSend={async ({ email, email2, email3, email4, email5, type = "invoice" }) => {
      try {
        const emailData = buildEmailData({ invoice, account, practice });

        // Collect all non-empty emails into an array
        const emails = [email, email2, email3, email4, email5]
          .map((e) => (e || "").trim())
          .filter(Boolean);

        const result = await sendInvoiceByEmail({
          emails,
          pdfBlob: statementBlob,
          type,
          accountId: account?._id,
          emailData,
        });
        console.log("ED", emailData);
        if (result?.error) throw new Error(result.error);
      } catch (err) {
        console.error("⛔ Failed to send email:", err);
        throw err;
      }
    }}
  />
)}

      {liabilityModalShow && (
        <ChangeLiability
          show={liabilityModalShow}
          onHide={() => setLiabilityModalShow(false)}
          lines={invoice?.lines}
          invoiceId={invoice?._id}
          invoice={invoice}
          claimId={claim?._id}
          claim={claim}
        />
      )}
    </div>
  );
}
